import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Search, ChevronDown, Globe,
  // Icons referenced by data registries
  Building2, Workflow, Users, FileText, Megaphone, Wrench, MessageSquare,
  Plug, ShieldCheck, Sparkles, TrendingUp, Wallet, BookOpen, Mail,
  CalendarDays, Quote, Home, BarChart3, ClipboardCheck, Store, Languages,
  Lock, AlertTriangle, History, CheckCircle2,
} from 'lucide-react';
import RevaLogo from '@/components/RevaLogo';
import T from '@/components/landing/copy';
import { tt } from '@/components/landing/atoms';
import { MEGAMENU, PRODUCTS_BY_SLUG } from '@/components/landing/products';

// Icon lookup table — products + menus use string keys
const ICON = {
  Building2, Workflow, Users, FileText, Megaphone, Wrench, MessageSquare,
  Plug, ShieldCheck, Sparkles, TrendingUp, Wallet, BookOpen, Mail,
  CalendarDays, Quote, Home, BarChart3, ClipboardCheck, Store, Languages,
  Lock, AlertTriangle, History, CheckCircle2,
};

// ─────────────────────────────────────────────────────────────────────────────
// AppFolio-inspired top nav with mega-menu dropdowns.
// Clean / light / mature / enterprise.
// ─────────────────────────────────────────────────────────────────────────────
export default function TopNavMega({ lang, setLang, onDemo, onTour }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null); // 'products' | 'why' | 'resources' | 'login' | null
  const [searchOpen, setSearchOpen] = useState(false);
  const rootRef = useRef(null);
  const isAr = lang === 'ar';

  // Close menus on outside click / escape
  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(null); setSearchOpen(false); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, []);

  const NavTrigger = ({ id, label, testid }) => (
    <button
      type="button"
      onMouseEnter={() => setOpen(id)}
      onClick={() => setOpen(open === id ? null : id)}
      className={`inline-flex items-center gap-1 text-[13.5px] font-medium transition-colors px-1 py-1.5 ${
        open === id ? 'text-[#0E1320]' : 'text-[#2A3340] hover:text-[#0E1320]'
      }`}
      data-testid={testid}
      aria-expanded={open === id}
    >
      {label}
      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === id ? 'rotate-180 text-[color:var(--reva-signal)]' : 'text-[#71777E]'}`} />
    </button>
  );

  return (
    <header
      ref={rootRef}
      onMouseLeave={() => setOpen(null)}
      className="sticky top-0 z-50 backdrop-blur-xl border-b border-[#E6EAF0] bg-[rgba(255,255,255,0.92)]"
      data-testid="reva-topnav"
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={onTour} className="flex items-center" data-testid="reva-nav-logo" aria-label="REVA">
          <RevaLogo variant="full" size={28} theme="light" />
        </button>

        {/* Primary nav */}
        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          <NavTrigger id="products"   label={isAr ? 'المنتجات' : 'Products'}   testid="reva-nav-products" />
          <NavTrigger id="why"        label={isAr ? 'لماذا REVA' : 'Why REVA'} testid="reva-nav-why" />
          <NavTrigger id="resources"  label={isAr ? 'المصادر'   : 'Resources'} testid="reva-nav-resources" />
        </nav>

        {/* Right side: search + login + demo CTA + lang */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen(s => !s)}
            className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full text-[#4A5263] hover:bg-[#F1F4F7] transition-colors"
            data-testid="reva-nav-search"
            aria-label={isAr ? 'بحث' : 'Search'}
          >
            <Search className="h-4 w-4" />
          </button>

          <div className="relative" data-testid="reva-login-wrapper">
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(open === 'login' ? null : 'login'); }}
              className="hidden sm:inline-flex items-center gap-1 text-[13px] font-medium text-[#2A3340] hover:text-[#0E1320] px-3 py-1.5 rounded-full transition-colors"
              data-testid="reva-nav-login"
              aria-expanded={open === 'login'}
            >
              {isAr ? 'تسجيل الدخول' : 'Login'}
              <ChevronDown className={`h-3 w-3 transition-transform ${open === 'login' ? 'rotate-180' : ''}`} />
            </button>

            {/* Login dropdown — click-only (not hover), anchored to button, single instance */}
            {open === 'login' && (
              <>
                {/* Click-outside backdrop */}
                <button
                  type="button"
                  aria-hidden="true"
                  onClick={() => setOpen(null)}
                  className="fixed inset-0 z-40 cursor-default"
                  style={{ background: 'transparent' }}
                  tabIndex={-1}
                />
                <div
                  className="absolute end-0 top-full mt-2 w-[280px] rounded-2xl border border-[#E6EAF0] bg-white shadow-2xl z-50 py-2"
                  data-testid="reva-megamenu-login"
                  role="menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  {MEGAMENU.login.items.map(item => {
                    const Icon = ICON[item.icon] || Lock;
                    return (
                      <button
                        key={item.slug}
                        onClick={() => { setOpen(null); navigate(item.slug); }}
                        className="w-full inline-flex items-center gap-3 px-3 py-2.5 text-start hover:bg-[#F7FBF9] transition-colors"
                        data-testid={`megamenu-login-${item.slug.replace('/login/', '').replace(/\//g, '-')}`}
                      >
                        <span className="h-7 w-7 rounded-full bg-[#F1F4F7] flex items-center justify-center shrink-0">
                          <Icon className="h-3.5 w-3.5 text-[#00734A]" strokeWidth={1.7} />
                        </span>
                        <span className="text-[13px] font-medium text-[#0E1320]">{isAr ? item.ar : item.en}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
            className="text-[11px] font-bold tracking-[0.18em] text-[#4A5263] hover:text-[#0E1320] px-2.5 py-1.5 rounded-full transition-colors reva-mono"
            data-testid="reva-nav-lang"
            aria-label="Toggle language"
          >
            <Globe className="h-3 w-3 inline me-1.5" />
            {lang === 'ar' ? 'EN' : 'AR'}
          </button>

          <button onClick={onDemo} className="reva-btn-primary text-[13px] py-2 px-4" data-testid="reva-nav-demo">
            {isAr ? 'احجز عرضًا' : 'Book a Demo'} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Search bar (inline) */}
      {searchOpen && (
        <div className="border-t border-[#E6EAF0] bg-white" data-testid="reva-nav-search-panel">
          <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-3 flex items-center gap-3">
            <Search className="h-4 w-4 text-[#71777E]" />
            <input
              autoFocus
              placeholder={isAr ? 'ابحث في المنتجات، الميّزات، الدّعم…' : 'Search products, features, support…'}
              className="flex-1 bg-transparent text-[14px] text-[#0E1320] placeholder:text-[#9CA3AF] outline-none"
              data-testid="reva-nav-search-input"
            />
            <button onClick={() => setSearchOpen(false)} className="text-[11px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">ESC</button>
          </div>
        </div>
      )}

      {/* ───────────── Products mega-menu ───────────── */}
      {open === 'products' && (
        <div className="absolute inset-x-0 top-16 border-t border-[#E6EAF0] bg-white shadow-xl" data-testid="reva-megamenu-products">
          <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-8 grid grid-cols-1 md:grid-cols-4 gap-6">
            {MEGAMENU.products.columns.map((col, i) => (
              <div key={i}>
                <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E] mb-3">
                  {tt(col.title, lang)}
                </div>
                <ul className="space-y-1">
                  {col.items.map(slug => {
                    const p = PRODUCTS_BY_SLUG[slug];
                    if (!p) return null;
                    const Icon = p.icon;
                    return (
                      <li key={slug}>
                        <button
                          onClick={() => { setOpen(null); navigate(`/products/${slug}`); }}
                          className="w-full text-start rounded-lg px-2 py-1.5 hover:bg-[#F7FBF9] transition-colors group"
                          data-testid={`megamenu-product-${slug}`}
                        >
                          <div className="flex items-start gap-2.5">
                            <Icon className="h-4 w-4 mt-0.5 text-[#00734A] shrink-0" strokeWidth={1.7} />
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-medium text-[#0E1320] group-hover:text-[#00734A]">{tt(p.short, lang)}</div>
                              <div className="text-[11.5px] text-[#4A5263] leading-snug truncate">{tt(p.blurb, lang)}</div>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-[#F1F4F7] bg-[#FBFCFD]">
            <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center justify-end gap-2">
              <button onClick={() => { setOpen(null); navigate('/products/performance-platform'); }} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#00734A] hover:gap-2 transition-all" data-testid="megamenu-products-cta">
                {isAr ? 'استكشف المنصّة' : 'Explore the platform'} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────── Why REVA dropdown ───────────── */}
      {open === 'why' && (
        <div className="absolute inset-x-0 top-16 border-t border-[#E6EAF0] bg-white shadow-xl" data-testid="reva-megamenu-why">
          <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {MEGAMENU.why.items.map(item => {
              const Icon = ICON[item.icon] || ShieldCheck;
              return (
                <button
                  key={item.slug}
                  onClick={() => { setOpen(null); navigate(`/why/${item.slug}`); }}
                  className="rounded-xl border border-[#E6EAF0] p-3 text-start hover:border-[color:var(--reva-signal)]/40 hover:bg-[#F7FBF9] transition-all"
                  data-testid={`megamenu-why-${item.slug}`}
                >
                  <Icon className="h-4.5 w-4.5 text-[#00734A]" strokeWidth={1.7} />
                  <div className="mt-2 text-[13px] font-medium text-[#0E1320]">{isAr ? item.ar : item.en}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ───────────── Resources dropdown ───────────── */}
      {open === 'resources' && (
        <div className="absolute inset-x-0 top-16 border-t border-[#E6EAF0] bg-white shadow-xl" data-testid="reva-megamenu-resources">
          <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {MEGAMENU.resources.items.map(item => {
              const Icon = ICON[item.icon] || BookOpen;
              return (
                <button
                  key={item.slug}
                  onClick={() => { setOpen(null); navigate(`/resources/${item.slug}`); }}
                  className="rounded-xl border border-[#E6EAF0] p-3 text-start hover:border-[color:var(--reva-signal)]/40 hover:bg-[#F7FBF9] transition-all"
                  data-testid={`megamenu-resource-${item.slug}`}
                >
                  <Icon className="h-4.5 w-4.5 text-[#00734A]" strokeWidth={1.7} />
                  <div className="mt-2 text-[13px] font-medium text-[#0E1320]">{isAr ? item.ar : item.en}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Login dropdown is rendered inline next to the Login button above (single source of truth). */}
    </header>
  );
}
